import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CtaButton } from "./CtaButton";
import { HeroComposite } from "./HeroComposite";
import TextAnimation from "@/components/ui/staggerText";
import { SCREEN_MANIFEST } from "@/generated/screenManifest.mjs";

const SCREENS = [
  {
    key: "im-safe",
    chip: "I'm Safe check-in",
    title: "One tap says “I'm safe”",
    text: "A single tap sends an immediate check-in to linked guardians. No call, no typing — reassurance in under a second.",
    alt: "NISCHINT app — My Protection screen with the I'm Safe check-in button",
  },
  {
    key: "family-circle",
    chip: "Family Circle",
    title: "The whole family, quietly in the loop",
    text: "Guardians see safety updates as they happen — who's online, who's protected, what's been acknowledged — without anyone having to ask.",
    alt: "NISCHINT app — Family Circle dashboard with guardians and today's activity",
  },
  {
    key: "safety-zones",
    chip: "Safety Zones",
    title: "Safe zones and routes that watch themselves",
    text: "Set the places and paths that matter — home, school, the daily commute. NISCHINT learns the routine and only speaks up when something breaks it.",
    alt: "NISCHINT app — Safe Route map with guardians watching",
  },
  {
    key: "alert-center",
    chip: "Alert Center & SOS",
    title: "Alerts that escalate until someone answers",
    text: "A missed check-in becomes a warning; an SOS becomes an escalation — with one-tap calling and location, straight to the people who can act.",
    alt: "NISCHINT app — Alert Center with an SOS emergency card",
  },
];

const DISCLOSURE = "Illustrative data — demo family, for preview only.";

// A beat's screen renders only when its file exists in public/ (build-time
// manifest — off-spec screens were removed; a replacement dropped at the
// same path returns with no code change). While ANY screen is missing, the
// story uses the stacked layout (each beat self-contained, no sticky phone
// column) so there is never a blank panel beside a beat.
const hasScreen = (key) => {
  const p = `/screens/${key}.jpg`;
  return p in SCREEN_MANIFEST ? SCREEN_MANIFEST[p] : true;
};
const ALL_SCREENS_PRESENT = SCREENS.every((s) => hasScreen(s.key));

const ScreenImage = ({ screen, eager }) => (
  <picture>
    <source srcSet={`/screens/${screen.key}.webp`} type="image/webp" />
    <img
      src={`/screens/${screen.key}.jpg`}
      alt={screen.alt}
      width={750}
      height={1625}
      loading={eager ? "eager" : "lazy"}
      fetchpriority={eager ? "high" : "auto"}
      className="w-full h-full object-cover object-top"
    />
  </picture>
);

const WEBPAGE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "NISCHINT — AI-powered urban safety for your loved ones",
  url: "https://nischint.care/",
  isPartOf: { "@type": "WebSite", name: "NISCHINT", url: "https://nischint.care" },
  about: { "@type": "Organization", name: "NISCHINT", legalName: "Skybyte Venture Private Limited" },
};

export const ScrollHero = () => {
  // active=0 by default: the first screen renders visible with no JS logic run,
  // so a failed/absent IntersectionObserver never leaves the hero blank.
  const [active, setActive] = useState(0);
  const [stacked, setStacked] = useState(false);
  const rm = useReducedMotion();

  // Hero entrance: eyebrow → heading → subtext → CTA, ~80-100ms apart,
  // 500ms ease-out each. Reduced-motion users get everything instantly.
  const reveal = (delay) =>
    rm
      ? {}
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.5, delay, ease: "easeOut" },
        };
  const beatRefs = useRef([]);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "webpage-jsonld";
    script.text = JSON.stringify(WEBPAGE_JSONLD);
    document.head.appendChild(script);

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || !("IntersectionObserver" in window)) {
      setStacked(true);
      return () => document.getElementById("webpage-jsonld")?.remove();
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(Number(entry.target.dataset.beat));
        });
      },
      { rootMargin: "-40% 0px -40% 0px" }
    );
    beatRefs.current.forEach((el) => el && observer.observe(el));
    return () => {
      observer.disconnect();
      document.getElementById("webpage-jsonld")?.remove();
    };
  }, []);

  const stackedMode = stacked || !ALL_SCREENS_PRESENT;
  const showInline = stackedMode; // stacked: every beat carries its own screen

  return (
    <section data-testid="scroll-hero" className="max-w-6xl mx-auto px-6 pt-12 sm:pt-16">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.p
            {...reveal(0)}
            className="text-sm font-semibold text-[#1E88E5] mb-4"
          >
            Launching first in Mumbai, Thane, Bengaluru, Delhi and Jaipur
          </motion.p>
          <motion.h1
            {...(rm
              ? {}
              : {
                  // LCP candidate on mobile: slide-only, no opacity-0 start —
                  // a hidden first paint doesn't count as LCP until it fades in.
                  initial: { y: 16 },
                  animate: { y: 0 },
                  transition: { duration: 0.5, delay: 0.08, ease: "easeOut" },
                })}
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-[#14171A]"
          >
            {rm ? (
              "AI-powered urban safety for your loved ones"
            ) : (
              <TextAnimation delay={0.15}>
                AI-powered urban safety for your loved ones
              </TextAnimation>
            )}
          </motion.h1>
          <motion.p
            {...reveal(0.16)}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#4A453C]"
          >
            NISCHINT watches over the people you worry about — your daughter on
            her late commute, your father on his morning walk — with smart alerts
            that speak up only when it matters. No constant tracking. Just quiet
            reassurance.
          </motion.p>
          <motion.div
            {...reveal(0.26)}
            className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <CtaButton
              testId="hero-cta-button"
              onClick={() => (window.location.href = "/plans")}
            >
              Get Started
            </CtaButton>
            <p className="text-xs text-[#726B5E]">
              Start with a 7-day free trial at launch.
            </p>
          </motion.div>
        </div>
        <div>
          <HeroComposite />
        </div>
      </div>

      <div className="mt-20 sm:mt-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#14171A]">
            See NISCHINT in action
          </h2>
          <p className="mt-3 text-base sm:text-lg text-[#4A453C]">
            Design preview with demo data. The app may look different at launch.
          </p>
          <p data-testid="hero-disclosure" className="mt-1 text-xs text-[#726B5E]">
            {DISCLOSURE}
          </p>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-2 gap-12">
          <div>
            {SCREENS.map((screen, i) => (
              <div
                key={screen.key}
                data-testid={`screen-beat-${i}`}
                data-beat={i}
                ref={(el) => (beatRefs.current[i] = el)}
                className={`${hasScreen(screen.key) ? "lg:min-h-[85vh]" : ""} flex flex-col justify-center py-10 lg:py-0`}
              >
                <span
                  className={`inline-flex self-start px-3 py-1.5 rounded-full text-xs font-semibold border border-[#E3DBC9] transition-colors duration-300 ${
                    active === i && !stackedMode ? "bg-white text-[#16C784]" : "bg-white/60 text-[#726B5E]"
                  }`}
                >
                  {screen.chip}
                </span>
                <h3 className="mt-4 text-2xl sm:text-3xl tracking-tight font-bold text-[#14171A]">
                  {screen.title}
                </h3>
                <p className="mt-3 text-base sm:text-lg leading-relaxed text-[#4A453C] max-w-md">
                  {screen.text}
                </p>
                {hasScreen(screen.key) && (
                  <div
                    data-testid={`inline-screen-${screen.key}`}
                    className={`mt-6 w-[240px] sm:w-[280px] rounded-[2rem] border-8 border-[#14171A] bg-[#14171A] shadow-[0_12px_40px_rgba(20,23,26,0.18)] overflow-hidden ${
                      showInline ? "" : "lg:hidden"
                    } ${showInline ? "" : "block"} aspect-[9/19.5]`}
                  >
                    <ScreenImage screen={screen} eager={i === 0} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {!stackedMode && (
            <div className="hidden lg:block">
              <div className="sticky top-24 flex flex-col items-center">
                <div
                  data-testid="phone-mockup"
                  className="relative w-[300px] xl:w-[330px] rounded-[3rem] border-[10px] border-[#14171A] bg-[#14171A] shadow-[0_20px_60px_rgba(20,23,26,0.25)]"
                >
                  <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#14171A] rounded-full z-10" />
                  <div className="relative rounded-[2.3rem] overflow-hidden aspect-[9/19.5] bg-white">
                    {SCREENS.map((screen, i) => (
                      <div
                        key={screen.key}
                        data-testid={`phone-screen-${screen.key}`}
                        className={`absolute inset-0 transition-opacity duration-700 ${
                          active === i ? "opacity-100" : "opacity-0"
                        }`}
                      >
                        <ScreenImage screen={screen} eager={i === 0} />
                      </div>
                    ))}
                  </div>
                </div>
                <p className="mt-4 text-xs text-[#726B5E]">{DISCLOSURE}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
